import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export type TableShareConfigType = "expiring" | "multi-use";

export interface TableShareConfigValue {
  expirationTimeSpan?: string;
  maxUseCount?: number;
}

interface TableShareConfigModalProps {
  isOpen: boolean;
  isLoading: boolean;
  type: TableShareConfigType | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (value: TableShareConfigValue) => void;
}

type DurationUnit = "minute" | "hour" | "day";

const durationUnitOptions: Array<{ value: DurationUnit; label: string }> = [
  { value: "minute", label: "دقیقه" },
  { value: "hour", label: "ساعت" },
  { value: "day", label: "روز" }
];

const toTimeSpan = (value: number, unit: DurationUnit) => {
  const totalMinutes =
    unit === "day" ? value * 24 * 60 : unit === "hour" ? value * 60 : value;

  const totalSeconds = totalMinutes * 60;
  const days = Math.floor(totalSeconds / 86400);
  const remainingAfterDays = totalSeconds % 86400;
  const hours = Math.floor(remainingAfterDays / 3600);
  const remainingAfterHours = remainingAfterDays % 3600;
  const minutes = Math.floor(remainingAfterHours / 60);
  const seconds = remainingAfterHours % 60;

  const dayPart = days.toString().padStart(2, "0");
  const hourPart = hours.toString().padStart(2, "0");
  const minutePart = minutes.toString().padStart(2, "0");
  const secondPart = seconds.toString().padStart(2, "0");

  return `${dayPart}.${hourPart}:${minutePart}:${secondPart}`;
};

const TableShareConfigModal: React.FC<TableShareConfigModalProps> = ({
  isOpen,
  isLoading,
  type,
  onOpenChange,
  onSubmit
}) => {
  const [durationValue, setDurationValue] = useState<number>(1);
  const [durationUnit, setDurationUnit] = useState<DurationUnit>("day");
  const [maxUseCount, setMaxUseCount] = useState<number>(1);

  useEffect(() => {
    if (!isOpen) return;

    setDurationValue(1);
    setDurationUnit("day");
    setMaxUseCount(1);
  }, [isOpen, type]);

  const title = useMemo(() => {
    if (type === "expiring") return "تنظیم زمان انقضای لینک";
    if (type === "multi-use") return "تنظیم تعداد دفعات استفاده";

    return "تنظیمات اشتراک گذاری";
  }, [type]);

  const description = useMemo(() => {
    if (type === "expiring")
      return "مدت اعتبار لینک را مشخص کنید تا پس از آن، لینک غیرقابل استفاده شود.";

    if (type === "multi-use")
      return "تعداد دفعات مجاز استفاده از لینک را وارد کنید.";

    return "تنظیمات مورد نظر را تعیین کنید.";
  }, [type]);

  const handleSubmit = () => {
    if (type === "expiring") {
      if (!durationValue || durationValue <= 0) return;

      onSubmit({
        expirationTimeSpan: toTimeSpan(durationValue, durationUnit)
      });
      return;
    }

    if (type === "multi-use") {
      if (!maxUseCount || maxUseCount <= 0) return;

      onSubmit({ maxUseCount });
    }
  };

  const isSubmitDisabled =
    isLoading ||
    !type ||
    (type === "expiring" ? durationValue <= 0 : maxUseCount <= 0);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-right">
          <DialogTitle className="text-right">{title}</DialogTitle>
          <DialogDescription className="text-right">
            {description}
          </DialogDescription>
        </DialogHeader>

        {type === "expiring" ? (
          <div className="grid gap-3">
            <label className="text-right text-sm text-slate-700">
              مدت اعتبار لینک
            </label>
            <div className="grid grid-cols-[1fr_120px] gap-3">
              <Input
                dir="ltr"
                type="number"
                min={1}
                value={durationValue}
                onChange={(event) =>
                  setDurationValue(Number(event.target.value))
                }
                placeholder="1"
                className="text-left"
              />
              <select
                value={durationUnit}
                onChange={(event) =>
                  setDurationUnit(event.target.value as DurationUnit)
                }
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-right outline-none transition-colors focus:border-primary"
              >
                {durationUnitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {type === "multi-use" ? (
          <div className="grid gap-3">
            <label className="text-right text-sm text-slate-700">
              تعداد دفعات مجاز استفاده
            </label>
            <Input
              dir="ltr"
              type="number"
              min={1}
              value={maxUseCount}
              onChange={(event) => setMaxUseCount(Number(event.target.value))}
              placeholder="1"
              className="text-left"
            />
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-start">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            ثبت تنظیمات
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TableShareConfigModal;
